import React, { useState } from 'react'
import { detectVideoPlatform, getVideoEmbedProps, getVideoThumbnailUrl } from 'utils/videoUtils'

const URL_REGEX = /(https?:\/\/[^\s<]+)/g

function AudioEmbed({ url }) {
  const mediaInfo = detectVideoPlatform(url)
  if (!mediaInfo) return null
  const embedProps = getVideoEmbedProps(mediaInfo)
  return (
    <div className="my-3 w-full rounded-xl overflow-hidden" style={{ height: '140px' }}>
      <iframe
        {...embedProps}
        className="w-full h-full"
        allow="autoplay; clipboard-write; encrypted-media"
      />
    </div>
  )
}

function VideoEmbed({ url }) {
  const videoInfo = detectVideoPlatform(url)
  if (!videoInfo) return null
  const embedProps = getVideoEmbedProps(videoInfo)
  return (
    <div className="my-3 w-full rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
      <iframe
        {...embedProps}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />
    </div>
  )
}

function VideoThumbnail({ url }) {
  const [playing, setPlaying] = useState(false)
  const videoInfo = detectVideoPlatform(url)
  if (!videoInfo) return null

  if (playing) {
    const embedProps = getVideoEmbedProps(videoInfo)
    return (
      <div className="my-2 rounded-lg overflow-hidden" style={{ maxWidth: '480px', aspectRatio: '16/9' }} onClick={(e) => e.stopPropagation()}>
        <iframe
          {...embedProps}
          src={embedProps.src + (embedProps.src.includes('?') ? '&' : '?') + 'autoplay=1'}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </div>
    )
  }

  const thumbnailUrl = getVideoThumbnailUrl(videoInfo.platform, videoInfo.videoId)
  return (
    <div className="block my-2 relative rounded-lg overflow-hidden cursor-pointer" style={{ maxWidth: '480px' }}
      onClick={(e) => { e.stopPropagation(); setPlaying(true) }}>
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt={videoInfo.platformName} className="w-full rounded-lg" />
      ) : (
        <div className="w-full rounded-lg bg-gray-100 flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
          <span className="text-gray-400 text-sm">{videoInfo.platformName}</span>
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg width="68" height="48" viewBox="0 0 68 48">
          <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="#FF0000" />
          <path d="M45 24L27 14v20" fill="#FFF" />
        </svg>
      </div>
    </div>
  )
}

export { VideoThumbnail }

// embedVideos: false = plain link, "compact" = thumbnail, true = full iframe
export default function Linkify({ text, embedVideos = true }) {
  if (!text) return null
  const parts = text.split(URL_REGEX)
  return parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) return part
    const videoInfo = detectVideoPlatform(part)
    if (videoInfo && videoInfo.type === 'audio' && embedVideos) {
      return <AudioEmbed key={i} url={part} />
    }
    if (videoInfo && embedVideos === true) {
      return <VideoEmbed key={i} url={part} />
    }
    if (videoInfo && embedVideos === 'compact') {
      return <VideoThumbnail key={i} url={part} />
    }
    return (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
         className="text-blue-600 hover:underline break-all">
        {part}
      </a>
    )
  })
}
